import { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, MapPin, Calendar, Donut } from "lucide-react";
import { FULL_LOGO_URL, FACEBOOK_URL, INSTAGRAM_URL, NAV_LINKS } from "@/data/content";
import { PolicyDialog } from "@/components/PolicyDialog";
import { PRIVACY_POLICY, COOKIE_POLICY } from "@/data/legal";
import { openCookieSettings } from "@/components/CookieConsent";

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Footer = () => {
  const [policy, setPolicy] = useState(null);

  return (
    <footer data-testid="footer" className="bg-[#111111] text-[#f7f0eb] px-5 md:px-10 pt-16 pb-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-12 justify-between">
          <div className="max-w-sm">
            <div className="flex items-center gap-3">
              <img src={FULL_LOGO_URL} alt="Bobo's Donuts" className="h-14 w-14 md:h-28 md:w-28 rounded-full object-cover ring-2 ring-[#ff648b]" />
              <span className="font-display font-bold text-2xl">Bobo's Donuts<sup className="align-[0.42em] text-[0.5em] font-semibold">™</sup></span>
            </div>
            <p className="text-[#f7f0eb]/70 mt-4 leading-relaxed">
              Hand-cut donuts from Mattawan, Michigan — building a more inclusive
              community, one sweet treat at a time.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-12">
            <div>
              <h4 className="font-display font-semibold text-lg mb-4">Explore</h4>
              <ul className="space-y-2.5">
                <li>
                  <Link
                    to="/about"
                    data-testid="footer-nav-about"
                    className="text-[#f7f0eb]/70 hover:text-[#ff648b] transition-colors"
                  >
                    About
                  </Link>
                </li>
                {NAV_LINKS.map((l) => (
                  <li key={l.id}>
                    <button
                      data-testid={`footer-nav-${l.id}`}
                      onClick={() => scrollTo(l.id)}
                      className="text-[#f7f0eb]/70 hover:text-[#ff648b] transition-colors"
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col gap-3">
              <button
                data-testid="footer-find-truck-btn"
                onClick={() => scrollTo("locations")}
                className="px-6 py-3 rounded-full bg-[#ff648b] text-[#111111] font-bold hover:bg-[#ed4a76] transition-all flex items-center gap-2 justify-center"
              >
                <MapPin className="h-4 w-4" /> Find the Truck
              </button>
              <div
                data-testid="footer-social-btn"
                className="flex items-stretch rounded-full overflow-hidden bg-white/90 border-2 border-[#111111]/15 shadow-lg hover:scale-105 transition-transform"
              >
                <span className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-[#111111] font-bold whitespace-nowrap">
                  <Donut className="h-5 w-5" /> Follow the Truck
                </span>
                <div className="w-px self-stretch bg-[#111111]/15" />
                <a
                  data-testid="footer-facebook-link"
                  href={FACEBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Bobo's on Facebook"
                  className="flex items-center justify-center px-5 py-3 text-[#111111] hover:bg-[#1877F2] hover:text-white transition-colors"
                >
                  <Facebook className="h-5 w-5" />
                </a>
                <div className="w-px self-stretch bg-[#111111]/15" />
                <a
                  data-testid="footer-instagram-link"
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Bobo's on Instagram"
                  className="flex items-center justify-center px-5 py-3 text-[#111111] hover:text-white hover:bg-[linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)] transition-colors"
                >
                  <Instagram className="h-5 w-5" />
                </a>
              </div>
              <button
                data-testid="footer-book-truck-btn"
                onClick={() => scrollTo("contact")}
                className="px-6 py-3 rounded-full bg-transparent border-2 border-[#ff648b] text-[#f7f0eb] font-bold hover:bg-[#ff648b] hover:text-[#111111] transition-all flex items-center gap-2 justify-center"
              >
                <Calendar className="h-4 w-4" /> Book the Truck
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col items-center gap-3">
          <div className="text-center text-sm text-[#f7f0eb]/50 flex items-center justify-center gap-1.5">
            <span>© {new Date().getFullYear()} Bobo's Donuts™ · Made with</span>
            <img
              src="/asl-love-header.png?v=2"
              alt="ASL sign for I love you"
              data-testid="footer-asl-love"
              className="inline-block h-[1.1em] w-auto select-none"
            />
            <span>in Mattawan, MI</span>
          </div>
          <div className="flex items-center justify-center gap-4 text-sm">
            <button
              data-testid="footer-privacy-link"
              onClick={() => setPolicy("privacy")}
              className="text-[#f7f0eb]/60 hover:text-[#ff648b] transition-colors"
            >
              Privacy Policy
            </button>
            <span className="text-white/20">·</span>
            <button
              data-testid="footer-cookie-link"
              onClick={() => setPolicy("cookie")}
              className="text-[#f7f0eb]/60 hover:text-[#ff648b] transition-colors"
            >
              Cookie Policy
            </button>
            <span className="text-white/20">·</span>
            <button
              data-testid="footer-cookie-settings-link"
              onClick={openCookieSettings}
              className="text-[#f7f0eb]/60 hover:text-[#ff648b] transition-colors"
            >
              Cookie Settings
            </button>
          </div>
        </div>

        <PolicyDialog
          open={policy === "privacy"}
          onOpenChange={(v) => !v && setPolicy(null)}
          policy={PRIVACY_POLICY}
        />
        <PolicyDialog
          open={policy === "cookie"}
          onOpenChange={(v) => !v && setPolicy(null)}
          policy={COOKIE_POLICY}
        />
      </div>
    </footer>
  );
};
